import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

/**
 * 🌟 SignedDocuments.js (Beautified)
 * -----------------------------------------
 * Displays all signed letters with:
 *  - Search & filter by company
 *  - Signed date, signer info
 *  - Download link for signed PDF
 *  - Clean modern card layout
 */

export const SignedDocuments = () => {
  const [letters, setLetters] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("All");

  /* -------------------------------------------------
     🔹 Fetch signed letters
  ---------------------------------------------------*/
  useEffect(() => {
    const q = query(
      collection(db, "letters"),
      where("status", "==", "signed"),
      orderBy("signedAt", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLetters(list);
      setFiltered(list);
      setLoading(false);

      const uniqueCompanies = [
        ...new Set(list.map((l) => l.companyName || "Unknown")),
      ];
      setCompanies(uniqueCompanies.sort());
    });

    return () => unsub();
  }, []);

  /* -------------------------------------------------
     🔹 Apply filters
  ---------------------------------------------------*/
  useEffect(() => {
    let data = letters;

    if (selectedCompany !== "All") {
      data = data.filter((l) => l.companyName === selectedCompany);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      data = data.filter(
        (l) =>
          l.subject?.toLowerCase().includes(keyword) ||
          l.referenceNo?.toLowerCase().includes(keyword)
      );
    }

    setFiltered(data);
  }, [search, selectedCompany, letters]);

  /* -------------------------------------------------
     🔹 Styles
  ---------------------------------------------------*/
  const container = {
    padding: "40px 20px",
    background: "#f7f9fb",
    minHeight: "100vh",
  };

  const card = {
    border: "1px solid #e0e0e0",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
    padding: "20px 26px",
    marginBottom: "18px",
    background: "#fff",
    maxWidth: "900px",
    marginLeft: "auto",
    marginRight: "auto",
    transition: "all 0.2s ease",
  };

  const cardHover = {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
  };

  /* -------------------------------------------------
     🔹 Loading / Empty State
  ---------------------------------------------------*/
  if (loading)
    return (
      <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
        ⏳ Loading signed letters...
      </div>
    );

  /* -------------------------------------------------
     🔹 Main Render
  ---------------------------------------------------*/
  return (
    <div style={container}>
      <h2
        style={{
          marginBottom: "25px",
          color: "#1a1a1a",
          textAlign: "center",
          fontWeight: 700,
          fontSize: "24px",
        }}
      >
        📜 Signed Letters Archive
      </h2>

      {/* 🔍 Filters */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          justifyContent: "center",
          marginBottom: "25px",
        }}
      >
        <input
          type="text"
          placeholder="Search by subject or reference..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            width: "260px",
            fontSize: "14px",
          }}
        />

        <select
          value={selectedCompany}
          onChange={(e) => setSelectedCompany(e.target.value)}
          style={{
            padding: "10px 14px",
            borderRadius: "8px",
            border: "1px solid #ccc",
            fontSize: "14px",
            minWidth: "180px",
          }}
        >
          <option value="All">All Companies</option>
          {companies.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      </div>

      {/* 🧾 Letter Cards */}
      {filtered.length === 0 ? (
        <p style={{ textAlign: "center", color: "#777" }}>
          No signed letters found.
        </p>
      ) : (
        filtered.map((l) => (
          <div
            key={l.id}
            style={card}
            onMouseEnter={(e) => Object.assign(e.currentTarget.style, cardHover)}
            onMouseLeave={(e) => Object.assign(e.currentTarget.style, card)}
          >
            {/* Header Row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                marginBottom: "8px",
              }}
            >
              <div>
                <strong style={{ fontSize: "15px", color: "#2c3e50" }}>
                  {l.companyName}
                </strong>
                <div style={{ fontSize: "13px", color: "#555" }}>
                  Ref: {l.referenceNo || "-"}
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: "13px", color: "#777" }}>
                  {l.signedAt?.toDate
                    ? new Date(l.signedAt.toDate()).toLocaleDateString("en-GB")
                    : "-"}
                </div>
                <div
                  style={{
                    color: "#2e7d32",
                    fontWeight: 600,
                    fontSize: "13px",
                  }}
                >
                  Signed By: {l.signedBy || "MD"}
                </div>
              </div>
            </div>

            {/* Subject */}
            <h4 style={{ margin: "10px 0", color: "#1976d2" }}>{l.subject}</h4>

            {/* Body Preview */}
            <div
              style={{
                color: "#444",
                fontSize: "14px",
                marginBottom: "18px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
              }}
              dangerouslySetInnerHTML={{ __html: l.body }}
            />

            {/* Download Button */}
            <div style={{ textAlign: "right" }}>
              {l.signedPdfUrl ? (
                <a
                  href={l.signedPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: "#1976d2",
                    color: "#fff",
                    padding: "10px 16px",
                    borderRadius: "8px",
                    textDecoration: "none",
                    fontWeight: 500,
                    fontSize: "14px",
                    transition: "0.2s",
                  }}
                >
                  ⬇️ Download Signed PDF
                </a>
              ) : (
                <span style={{ color: "#999" }}>No PDF available</span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
